import {
  fulfillProductByRxNumberApi,
  getOrdersApi,
  manageProductFulfillmentApi,
  overrideFulfillmentNdcApi,
} from 'api/order';
import { getRxByScanIdApi } from 'api/prescription';
import { generateBulkLabelsApi } from 'api/requests';
import { AddManualLotEnabled } from 'common/utils';
import { playSuccessBeep, playWarningBeep } from '../utils/beeps';
import printLabelViaJspm from '../utils/printLabel';
import {
  canCommitSession,
  commitBlockReason,
  hasValue,
  sessionHasHandRecordedVial,
  sessionHasNdcSubstitution,
} from './ScanSessionContext';
import { sessionHasLotChange, startActiveLabelLookup, takePrintedLabelInfo } from './printedLabel';

// Validate a scanned Rx bottle against POM. The decoded barcode (scan id) is
// the primary key; when no barcode was decoded — or the scan-id lookup comes
// back empty — fall back to looking the order up by its printed Rx number via
// listPomOrders. On success the authoritative record replaces the model-read
// fields; if neither path resolves, the scan is discarded and the operator is
// asked to re-scan.
const normalizeRx = (value) =>
  String(value ?? '')
    .replace(/\s+/g, '')
    .toUpperCase();

const validatedFromScanId = (rx) => ({
  rxNumber: rx.rxNumber,
  patientName: rx.patientName,
  drugName: rx.drugName,
  ...(hasValue(rx.strength) ? { strength: rx.strength } : {}),
  // Fallback for resolvePrescribedProductId when the listing has no lot for this NDC.
  ...(hasValue(rx.productId) ? { productId: rx.productId } : {}),
  orderId: rx.orderId,
  lineItemId: rx.lineItemId,
  quantity: rx.quantity,
  ndc: rx.ndc,
  // The LOT text printed on the scanned sheet (comma-joined, absent when the
  // label shows none) — see sessionHasLotChange.
  ...(hasValue(rx.lotNumber) ? { printedLotNumber: rx.lotNumber } : {}),
  partnerName: rx.partnerName,
  lineItemStatus: rx.lineItemStatus,
  orderStatus: rx.orderStatus,
  isScanned: rx.isScanned,
  // The pharmacy a hand-recorded (unverified) lot is claimed against — see AddDrugByLotDialog —
  // and the mpi manageProductFulfillmentStatus needs to record such a session (see
  // fulfillCommittedSession).
  fulfillingPharmacyId: rx.fulfillingPharmacyId,
  mpi: rx.mpi,
});

// listPomOrders now returns drugName + strength, so those authoritative fields
// replace the model-read values too (guards against the AI misreading the drug
// name). Only include a field when the record actually carries it, so a null
// value never wipes the scanned drug/strength.
const validatedFromOrder = (order) => ({
  rxNumber: order.rxNumber,
  patientName: order.patientName,
  ...(hasValue(order.drugName) ? { drugName: order.drugName } : {}),
  ...(hasValue(order.strength) ? { strength: order.strength } : {}),
  ...(hasValue(order.productId) ? { productId: order.productId } : {}),
  orderId: order.orderId,
  lineItemId: order.lineItemId,
  partnerName: order.partnerName,
  lineItemStatus: order.lineItemStatus,
  orderStatus: order.status,
  quantity: order.quantity,
  ndc: order.ndc,
  isScanned: order.isScanned,
  fulfillingPharmacyId: order.fulfillingPharmacyId,
  mpi: order.mpi,
});

const lookupByRxNumber = (rxNumber) =>
  getOrdersApi({ page: 1, filter: { rxNumber } })
    .then(({ orders }) => {
      const list = orders ?? [];
      const match = list.find((o) => normalizeRx(o.rxNumber) === normalizeRx(rxNumber)) ?? list[0];
      return match && hasValue(match.rxNumber) ? validatedFromOrder(match) : null;
    })
    .catch(() => null);

const resolveRxRecordFromPom = (parsed, notify) => {
  const sId = hasValue(parsed.barcode) ? parsed.barcode.trim() : null;
  const rxNumber = hasValue(parsed.rxNumber) ? parsed.rxNumber.trim() : null;
  if (!sId && !rxNumber) {
    notify?.('No barcode or Rx number detected — please scan it again.', 'error');
    return Promise.resolve(null);
  }
  // Fire BOTH lookups up front so they run in parallel. The scan-id lookup is
  // still authoritative: if it resolves to a real record we use it and ignore
  // the (already in-flight) Rx-number result. Only when scan-id comes back
  // empty do we fall back to the Rx-number result — which by then is already
  // running, so we no longer pay a second sequential round-trip.
  const byScanId = sId
    ? getRxByScanIdApi({ sId })
        .then((rx) =>
          // labelSId records WHICH printed sheet this session was opened from, so
          // fulfillCommittedSession can tell afterwards whether that sheet still
          // scans — see confirmPrintedLabelStillLive.
          rx && hasValue(rx.rxNumber) ? { ...validatedFromScanId(rx), labelSId: sId } : null
        )
        .catch(() => null)
    : Promise.resolve(null);
  const byRxNumber = rxNumber ? lookupByRxNumber(rxNumber) : Promise.resolve(null);
  return byScanId.then((byId) => {
    if (!byId) return byRxNumber;
    // The scan id matched a record. Barcodes occasionally misdecode to a value
    // that maps to a DIFFERENT real order — which would silently load the wrong
    // patient. So when the label also carried a printed Rx number and it
    // disagrees with the barcode-matched record, distrust the barcode: warn the
    // operator and prefer the printed-Rx-number lookup instead.
    if (rxNumber && normalizeRx(byId.rxNumber) !== normalizeRx(rxNumber)) {
      notify?.(
        `Barcode resolved to Rx ${byId.rxNumber}, but the printed Rx number is ${rxNumber}. Using the printed Rx number — please verify this is the correct prescription.`,
        'warning'
      );
      return byRxNumber.then((byNum) => byNum ?? byId);
    }
    return byId;
  });
};

// Resolve an authoritative POM record for a scanned Rx: try the decoded barcode
// (scan id) first, then fall back to the printed Rx number via listPomOrders.
// Resolves to the validated fields, or null when neither path finds a match.
// A record resolved by Rx number also starts the background label lookup (see
// printedLabel.js).
const resolveRxRecord = (parsed, notify) =>
  resolveRxRecordFromPom(parsed, notify).then((record) => {
    if (record && !hasValue(record.labelSId) && hasValue(record.lineItemId)) {
      startActiveLabelLookup(record.lineItemId);
    }
    return record;
  });

// Backend error codes thrown by fulfillProductByRxNumber/overrideFulfillmentNdc
// (see fulfillByRxNumberService.ts), mapped to operator-facing guidance. These
// are NOT all fixed the same way — some mean "re-scan the correct vial",
// others mean "this needs to be checked in POM first" — so a single blanket
// "re-scan and commit again" (the old behavior) was actively wrong for half
// of them, e.g. telling the operator to re-scan an Rx that's already scanned.
const FULFILLMENT_ERROR_GUIDANCE = {
  RX_ALREADY_SCANNED:
    'this Rx has already been scanned and fulfilled — verify in POM before doing anything else.',
  ASSIGNED_NDC_NOT_FOUND:
    'POM has no NDC assigned to this Rx yet — assign one in POM, then re-scan.',
  PRESCRIPTION_NOT_FOUND_ERROR:
    'no prescription was found for this Rx number in POM — verify the Rx number.',
  RX_LINE_ITEM_NOT_FOUND:
    'no fulfillable line item was found for this Rx in POM — check its order status.',
  REQUEST_NOT_FOUND: 'the order for this Rx could not be loaded in POM — check its order status.',
  SCANNED_NDC_MISMATCH:
    'the scanned NDC does not match the NDC assigned to this Rx in POM — re-scan the correct vial.',
  INVENTORY_LOT_NOT_FOUND:
    'the scanned lot was not found in inventory for this pharmacy, or is expired/depleted — re-scan the correct vial.',
  LOT_NDC_MISMATCH: 'the scanned lot belongs to a different NDC — re-scan the correct vial.',
  OVERRIDE_NDC_NOT_IN_INVENTORY:
    'the substituted NDC is not stocked at this pharmacy — re-scan the correct vial.',
  OVERRIDE_NDC_PRODUCT_MISMATCH:
    'the substituted NDC belongs to a different product — a different drug cannot be substituted here.',
  // manageProductFulfillmentStatus — the path a session with a hand-recorded (unverified) lot
  // takes; see fulfillCommittedSession.
  ORDER_STATE_ERROR: 'the order is no longer in a fulfillable state — check its status in POM.',
  PRODUCT_FULFILLMENT_ERROR:
    'the line item is not eligible for product fulfillment — check its status in POM.',
  INVENTORY_ITEM_NOT_FOUND:
    'inventory has no record of the hand-recorded NDC at this pharmacy — ask an inventory user to add the product, then commit again.',
  INVENTORY_LOT_BUD_AMBIGUOUS:
    'the hand-recorded lot number exists under more than one BUD — add it again with the BUD from the vial.',
};

const fulfillErrorMessage = (err) => {
  const code = err?.response?.errors?.[0]?.extensions?.code;
  if (code && FULFILLMENT_ERROR_GUIDANCE[code]) return FULFILLMENT_ERROR_GUIDANCE[code];
  // Unmapped/unexpected error — the old fallback behavior is still the best
  // available guidance here, since we genuinely don't know the cause.
  const rawMessage = err?.response?.errors?.[0]?.message || err?.message;
  return rawMessage
    ? `${rawMessage} — please re-scan the session and commit it again.`
    : 'Failed to record fulfillment — please re-scan the session and commit it again.';
};

// True when the session holds a vial recorded by hand in AddDrugByLotDialog's
// unverified-lot path. Gated on the same switch as that dialog
// (REACT_APP_ADD_MANUAL_LOT_ENABLED): with it off no session is ever treated as
// hand-recorded, so every commit keeps taking fulfillProductByRxNumber and its
// hard-fail lot contract — a lot inventory does not hold in stock is refused
// rather than claimed as unverified.
export const sessionHasManualLot = (session) =>
  AddManualLotEnabled && sessionHasHandRecordedVial(session);

// Vials sharing a lot # (case-insensitive) are merged into a single entry whose
// quantity is the sum of the vials' quantities. The merged entry's NDC comes
// from the first vial in the group that carries one. Shared by both payload
// builders below; a hand-recorded vial's manual-entry fields ride along on the
// merged entry so buildLotAllocations can forward them.
const mergeVialsByLot = (vials) => {
  const grouped = new Map();
  vials.forEach((v) => {
    const lotNumber = v.fields.lotNumber.trim();
    const ndc = hasValue(v.fields?.ndc) ? v.fields.ndc.trim() : null;
    const quantity = Number(v.fields.quantity);
    const key = lotNumber.toUpperCase();
    const entry = grouped.get(key);
    if (entry) {
      entry.quantity += quantity;
      if (!entry.ndc && ndc) entry.ndc = ndc;
      if (!entry.manual && v.fields.recordedManually) entry.manual = v.fields;
    } else {
      grouped.set(key, {
        ndc,
        lotNumber,
        quantity,
        manual: v.fields.recordedManually ? v.fields : null,
      });
    }
  });
  return [...grouped.values()];
};

// Payload for fulfillProductByRxNumber / overrideFulfillmentNdc — the ordinary
// scan path. DrugDispensedInput is ndc + lotNumber + quantity only.
export const buildDrugDispensed = (vials) =>
  mergeVialsByLot(vials).map(({ ndc, lotNumber, quantity }) => ({
    ...(ndc ? { ndc } : {}),
    lotNumber,
    quantity,
  }));

// Payload for manageProductFulfillmentStatus — the path a session with a
// hand-recorded vial takes, because only LotAllocationInput carries the
// manual-entry contract Fill Product sends (POM-6083): isManualEntry always;
// inventoryLotId when the operator claimed an existing depleted lot, else
// isNewLot; expirationDate (BUD) — required by the dialog — for a new lot, and
// for a picked lot that had none (suppliesBud); never for a picked lot that
// already carries one, where the API refuses it. An ordinary scanned vial in
// the same session goes out as a plain allocation with no lot id: the scan-app
// listing carries none, and core resolves it by lot number + NDC and allocates
// from stock exactly as the scan path would have.
export const buildLotAllocations = (vials) =>
  mergeVialsByLot(vials).map(({ ndc, lotNumber, quantity, manual }) => {
    const base = { inventoryLotId: null, lotNumber, quantity, ...(ndc ? { ndc } : {}) };
    if (!manual) return base;
    const inventoryLotId = hasValue(manual.inventoryLotId) ? manual.inventoryLotId : null;
    const expirationDate = hasValue(manual.expirationDate) ? manual.expirationDate : null;
    return {
      ...base,
      inventoryLotId,
      isManualEntry: true,
      ...(inventoryLotId
        ? { ...(manual.suppliesBud && expirationDate ? { expirationDate } : {}) }
        : { isNewLot: true, expirationDate }),
    };
  });

// A committed session that dispensed under a different (but same-product) NDC
// than the Rx was prescribed under leaves the already-printed label showing
// the WRONG NDC. overrideFulfillmentNdc (called just before this, for the
// same reason) already regenerates the label server-side once it records the
// substitution — so all that's needed here is to fetch that regenerated label
// and print it immediately, rather than relying on the operator to notice and
// print it manually. Same for a lot change (sessionHasLotChange), and when
// confirmPrintedLabelStillLive finds that core regenerated the label for some
// other reason — same remedy, different `reason` toast.
const regenerateAndPrintLabel = (session, committed, notify, reason) => {
  const lineItemId = committed.rx?.lineItemId;
  const rxNumber = committed.rx?.rxNumber;
  if (!lineItemId) return;

  session.setLabelStatus?.(committed.id, 'regenerating');
  notify?.(reason ?? `NDC differs for Rx ${rxNumber} — fetching regenerated label…`, 'info');

  generateBulkLabelsApi({ requestLineItemIds: [lineItemId] })
    .then((labelUrl) => {
      session.setLabelStatus?.(committed.id, 'regenerated');
      session.setPrintStatus?.(committed.id, 'printing');
      return printLabelViaJspm(labelUrl, notify)
        .then(() => {
          session.setPrintStatus?.(committed.id, 'printed');
          notify?.(`Regenerated label for Rx ${rxNumber} sent to printer.`, 'success');
        })
        .catch(() => {
          // printLabelViaJspm already told the operator the specific reason.
          session.setPrintStatus?.(committed.id, 'failed');
          playWarningBeep();
        });
    })
    .catch(() => {
      session.setLabelStatus?.(committed.id, 'failed');
      notify?.(
        `Failed to fetch the regenerated label for Rx ${rxNumber} — please print it manually.`,
        'error'
      );
      playWarningBeep();
    });
};

// Core can retire the printed label on a commit the portal did NOT ask a
// reprint for: fulfillProductByRxNumber regenerates when a matching scan
// refreshed a stale drug identity on the prescription override (see
// refreshOverrideDrugIdentity in fulfillByRxNumberService.ts), or when the
// lots changed but the sheet's LOT was not known here (see
// sessionHasLotChange), and nothing in the mutation's response says so. A
// regenerated label is a new label — new labelSId, new Code 128 — so the
// sheet in the operator's hand stops resolving
// and pharmacist verification on the iPad refuses it until someone reprints.
//
// So after a commit that did not already reprint, ask getRxByScanId whether the
// sheet this session was opened from still resolves. It did moments ago (that
// is how the session opened), so LABEL_NOT_FOUND now can only mean core
// superseded it — reprint. Anything else (network, timeout) is ignored: there
// is no evidence the sheet is dead, and a spurious reprint wastes a label.
// Needs the sheet's labelSId: from the barcode, or for a session opened by Rx
// number from the background label lookup (see takePrintedLabelInfo in
// printedLabel.js).
const confirmPrintedLabelStillLive = (session, committed, notify) => {
  const sId = committed.rx?.labelSId;
  if (!hasValue(sId)) return;
  getRxByScanIdApi({ sId }).catch((err) => {
    const code = err?.response?.errors?.[0]?.extensions?.code;
    if (code !== 'LABEL_NOT_FOUND') return;
    regenerateAndPrintLabel(
      session,
      committed,
      notify,
      `POM regenerated the label for Rx ${committed.rx?.rxNumber} — the printed one no longer scans. Fetching the new label…`
    );
  });
};

// Record a committed session against POM. Runs in the background so committing
// never blocks the operator; the session's history entry carries fulfillStatus
// (pending → success/failed). A failed entry cannot be retried — the operator
// re-scans the session and commits it again.
//
// Two routes:
//  - Ordinary session → fulfillProductByRxNumber (or overrideFulfillmentNdc for
//    a same-product NDC substitution). This is the scan path: it sets is_scanned,
//    writes the scan breadcrumb and validates the NDC against the assigned one.
//  - Session with a hand-recorded (unverified) lot → manageProductFulfillmentStatus
//    with lotAllocations, the same mutation Fill Product uses, because only
//    LotAllocationInput carries the manual-entry fields. `markScanned: true`
//    makes core apply the duplicate-scan guard and set is_scanned, so the Rx is
//    indistinguishable from one recorded by the scan path. A same-product NDC
//    substitution rides along: every allocation carries the dispensed NDC, and
//    core persists it on the fill, re-syncs the prescription override to it and
//    regenerates the label (POM-5826) — so the reprint below applies here too.
//
// Either way core regenerates the label when the dispensed NDC or lots differ
// from the ones the printed sheet names (the lot drives the BUD), and keeps it
// — barcode intact — when both match. So the portal reprints on an NDC
// substitution or a lot change, and a commit that did neither is followed by a
// check that the printed sheet still scans — see confirmPrintedLabelStillLive.
// A session opened by Rx number whose printed sheet could not be looked up
// (see takePrintedLabelInfo) prints the current label instead.
const fulfillCommittedSession = (session, committed, notify) => {
  session.setFulfillResult?.(committed.id, 'pending');
  const printedLabel = takePrintedLabelInfo(committed);
  const withLabel = { ...committed, rx: printedLabel.rx };
  // A vial dispensed under a different (but same-product) NDC than prescribed
  // is a substitution — record it via overrideFulfillmentNdc so POM re-points
  // the Rx's assigned NDC, instead of the ordinary fulfillment mutation, which
  // would reject a scanned NDC that doesn't match the one already assigned.
  const ndcSubstitution = sessionHasNdcSubstitution(committed);
  const request = sessionHasManualLot(committed)
    ? manageProductFulfillmentApi({
        lineItemId: committed.rx.lineItemId,
        mpi: committed.rx.mpi,
        orderId: committed.rx.orderId,
        state: 'PRODUCT_FULFILLED',
        lotAllocations: buildLotAllocations(committed.vials),
        unassignedFill: false,
        markScanned: true,
      })
    : (ndcSubstitution ? overrideFulfillmentNdcApi : fulfillProductByRxNumberApi)({
        rxNumber: committed.rx.rxNumber.trim(),
        drugDispensed: buildDrugDispensed(committed.vials),
      });
  return request
    .then(() => {
      session.setFulfillResult?.(committed.id, 'success');
      const { rxNumber } = committed.rx;
      if (ndcSubstitution) regenerateAndPrintLabel(session, withLabel, notify);
      else if (sessionHasLotChange(withLabel)) {
        regenerateAndPrintLabel(
          session,
          withLabel,
          notify,
          `Lot differs for Rx ${rxNumber} — fetching regenerated label…`
        );
      } else if (printedLabel.status === 'unknown') {
        // Cannot compare and cannot re-check — print the current label rather
        // than risk leaving the operator holding a sheet core has retired.
        regenerateAndPrintLabel(
          session,
          withLabel,
          notify,
          `Could not confirm the printed label for Rx ${rxNumber} is current — printing the latest label…`
        );
      } else confirmPrintedLabelStillLive(session, withLabel, notify);
    })
    .catch((err) => {
      const message = fulfillErrorMessage(err);
      session.setFulfillResult?.(committed.id, 'failed', message);
      notify?.(`Failed to record fulfillment for Rx ${committed.rx.rxNumber}: ${message}`, 'error');
      playWarningBeep();
    });
};

// Manual "Commit session" action: commit locally, then record the fulfillment
// against POM in the background.
export const commitSession = (session, notify) => {
  const committed = session.current;
  if (!committed || !canCommitSession(committed, session.inventory)) return;
  session.commitCurrent();
  fulfillCommittedSession(session, committed, notify);
};

// A container the model tagged as rx_bottle but that carries vial-only signals
// (a lot # or NDC) while lacking BOTH a prescription number and a decoded
// barcode is almost certainly a misread vial. Used to short-circuit the
// commit-and-swap path so a misread can't finalize the operator's open session.
const looksLikeMisreadVial = (parsed) =>
  (hasValue(parsed.lotNumber) || hasValue(parsed.ndc)) &&
  !hasValue(parsed.rxNumber) &&
  !hasValue(parsed.barcode);

// Statuses a scan station Rx may be in — deliberately the same set
// resolveFulfillableLineItem accepts (see fulfillByRxNumberService.ts), so the
// UI never opens a session the fulfillment call would reject. ORDER_VERIFICATION
// / PRODUCT_FULFILLED are the states an item bulk print already moved through —
// it is marked fulfilled (and its order elevated) before anyone physically scans
// it, so neither status can tell "not yet scanned" from "already scanned"
// (POM-5875). isScanned is the real duplicate-scan guard, and the backend
// rejects a second scan with RX_ALREADY_SCANNED regardless of this check.
// Deliberately never mirrored into local session history: sending an item back
// to the queue clears isScanned, and that can happen on another page or another
// station, so only the live record can answer whether this Rx is scannable.
const READY_ORDER_STATUSES = ['OPEN', 'ORDER_VERIFICATION', 'ORDER_RE_VERIFICATION'];
const READY_LINE_ITEM_STATUSES = ['PRODUCT_FULFILLMENT', 'PRODUCT_FULFILLED'];

const isReadyForFulfillment = (validated) =>
  READY_ORDER_STATUSES.includes(validated.orderStatus) &&
  READY_LINE_ITEM_STATUSES.includes(validated.lineItemStatus) &&
  !validated.isScanned;

const notReadyMessage = (validated) => {
  if (validated.isScanned) {
    return `Rx ${validated.rxNumber} has already been scanned — verify it in POM before doing anything else.`;
  }
  const problems = [];
  if (!READY_ORDER_STATUSES.includes(validated.orderStatus)) {
    problems.push(`order status: ${validated.orderStatus || 'unknown'}`);
  }
  if (!READY_LINE_ITEM_STATUSES.includes(validated.lineItemStatus)) {
    problems.push(`line item status: ${validated.lineItemStatus || 'unknown'}`);
  }
  return `Rx ${validated.rxNumber} is not ready for fulfillment — ${problems.join(', ')}.`;
};

const validateRxBottle = (session, parsed, scanId, notify) => {
  const sId = hasValue(parsed.barcode) ? parsed.barcode.trim() : null;
  const rxNumber = hasValue(parsed.rxNumber) ? parsed.rxNumber.trim() : null;
  if (!sId && !rxNumber) {
    notify?.('No barcode or Rx number detected — please scan it again.', 'error');
    session.failRxValidation?.(scanId);
    return;
  }
  session.markRxValidating?.(scanId);

  resolveRxRecord(parsed, notify).then((validated) => {
    if (!validated) {
      notify?.('Could not validate this Rx — please scan it again.', 'error');
      session.failRxValidation?.(scanId);
      return;
    }
    if (!isReadyForFulfillment(validated)) {
      notify?.(notReadyMessage(validated), 'error');
      session.failRxValidation?.(scanId);
      playWarningBeep();
      return;
    }
    session.applyRxValidation?.(scanId, validated);
    playSuccessBeep();
  });
};

// Reducer exported for use by hook that wires socket events.
// Two optional interceptors, both `(parsed, { scanId, timestamp }) => boolean`
// and both wired by RxScanStation/index.js. Each returns true when it took the
// read, and then owns ALL feedback for it (beep, toast):
//  - `onUnverifiedVial`: a vial read the scan app could NOT verify against its
//    inventory listing. The caller either adds the vial straight away (the lot
//    is on record in inventory) or offers the manual (unverified lot) path
//    pre-filled from the read. Falls through to the warning beep + toast.
//  - `onVerifiedVial`: a vial read the scan app DID verify. The listing it
//    verifies against carries depleted lots too (GET /inventory reports them
//    with quantity 0, which the listing drops), so a drained lot reads as an
//    ordinary vial here and is then refused at commit. The caller checks the
//    lot against inventory's depleted lots for the Rx's product and takes the
//    read as an unverified lot when it is one. Falls through to the ordinary
//    vial add.
export const wireScanEvent = (
  session,
  event,
  notify,
  { onUnverifiedVial, onVerifiedVial } = {}
) => {
  if (!event || typeof event !== 'object') return;
  // scan:error events from the scan app (hard failures with no parsed data)
  // are dropped silently — there is nothing for the operator to edit.
  if (event.type === 'scan:error') return;
  if (event.type !== 'scan:complete') return;
  const parsed = event.parsed ?? {};
  const containerType = parsed.containerType ?? 'unknown';

  if (containerType === 'rx_bottle') {
    const currentRxNum = session.current?.rx?.rxNumber;
    const isDuplicate = currentRxNum && currentRxNum === parsed.rxNumber;

    // No open session (or a duplicate of the current Rx): show the fields
    // immediately and validate against POM. The success beep fires only once
    // the record is confirmed (RX reads never warn audibly).
    if (!session.current || isDuplicate) {
      session.onRxScanned(parsed, event.scanId, event.timestamp);
      if (!isDuplicate) validateRxBottle(session, parsed, event.scanId, notify);
      return;
    }

    // A session is open. If it can't be committed yet, never swap it out.
    const reason = commitBlockReason(session.current, session.inventory);
    if (reason) {
      notify?.(`Cannot start a new Rx — ${reason}`, 'warning');
      return;
    }

    // The open session IS committable, so a new Rx would auto-commit it. Guard
    // against a misread vial (tagged rx_bottle) silently finalizing the
    // operator's session and getting lost: a vial that carries lot #/NDC but no
    // Rx number or barcode is almost certainly a misread — short-circuit
    // instantly (no POM round-trip) before touching the session.
    if (looksLikeMisreadVial(parsed)) {
      notify?.(
        'Ignored an unrecognized Rx scan — your current session is still open. If that was a vial, please re-scan it.',
        'warning'
      );
      playWarningBeep();
      return;
    }

    // Optimistic swap: commit the previous session and open the new one
    // IMMEDIATELY so the operator never waits on POM validation (which can take
    // several seconds). The new session shows a "Validating…" chip while the
    // lookup runs in the background. If validation fails, rollbackRxSwap
    // restores the previous session (re-parenting any vials scanned in the
    // meantime) so a misread bottle can never silently end a session.
    const displaced = session.current;
    session.onRxScanned(parsed, event.scanId, event.timestamp);
    session.markRxValidating?.(event.scanId);
    resolveRxRecord(parsed, notify).then((validated) => {
      if (!validated) {
        session.rollbackRxSwap?.(event.scanId);
        notify?.(
          'That scan was not a valid Rx — restored your previous session. If it was a vial, please re-scan it.',
          'warning'
        );
        playWarningBeep();
        return;
      }
      if (!isReadyForFulfillment(validated)) {
        session.rollbackRxSwap?.(event.scanId);
        notify?.(`${notReadyMessage(validated)} Restored your previous session.`, 'error');
        playWarningBeep();
        return;
      }
      // Genuine next-patient Rx: apply the authoritative POM fields to the
      // already-open session and confirm audibly. The displaced session's
      // commit is now final, so record its fulfillment against POM. (On the
      // rollback path above the session is restored un-committed, so nothing
      // is fulfilled.)
      session.applyRxValidation(event.scanId, validated);
      fulfillCommittedSession(session, displaced, notify);
      playSuccessBeep();
    });
    return;
  }

  // Unknown container types are dropped — the scan app alerts the operator
  // audibly so they re-scan; there's nothing useful to edit in the portal.
  if (containerType !== 'drug_vial') return;

  // An unverified read is no longer let into the session at all — the operator
  // must re-scan (or escalate) rather than dispense from an unconfirmed read.
  // "Unverified" here means the scan app found no in-stock lot matching the
  // read: the vial's lot is exactly what the manual (unverified lot) path
  // exists for, so the caller gets first refusal on it.
  if (parsed.unsure) {
    if (onUnverifiedVial?.(parsed, { scanId: event.scanId, timestamp: event.timestamp }) === true) {
      return;
    }
    playWarningBeep();
    notify?.('Unverified drug scan — please scan again or contact support.', 'error');
    return;
  }

  // No per-field name/NDC matching here — the vial's productId is checked
  // against the Rx's productId (resolvePrescribedProductId) at commit time
  // instead; see commitBlockReason.
  const missingLot = !hasValue(parsed.lotNumber);
  const missingDrug = !hasValue(parsed.drugName);
  if (missingLot || missingDrug) {
    // Vial fields aren't editable, so a vial missing either of these can never
    // be fixed after the fact — reject the scan instead of admitting a
    // permanently-incomplete row.
    playWarningBeep();
    const missing = [missingDrug && 'drug name', missingLot && 'lot #']
      .filter(Boolean)
      .join(' and ');
    notify?.(`Vial scan is missing a ${missing} — please scan again or contact support.`, 'error');
    return;
  }
  if (onVerifiedVial?.(parsed, { scanId: event.scanId, timestamp: event.timestamp }) === true) {
    return;
  }
  playSuccessBeep();
  session.onVialScanned(parsed, event.scanId, event.timestamp);
};
